import { IdleController } from '@components/index'
import * as Sentry from '@sentry/react'
import { getCurrentUser } from 'aws-amplify/auth'
import { NuqsAdapter } from 'nuqs/adapters/react'
import React from 'react'
import { QueryClientProvider } from 'react-query'
import { AvailContextProvider } from 'src/contexts/avail-context'

import B2BTourProvider from '@/components/tour/TourProvider'
import { Toaster } from '@/components/ui/toaster'
import { AuthContextProvider } from '@/contexts/auth-context'
import { FilesContextProvider } from '@/contexts/files-context'
import { AutocompleteProvider } from '@/contexts/map-autocomplete-context'
import { MapProvider } from '@/contexts/map-context'
import { MarkersProvider } from '@/contexts/markers-context'
import { SidebarContextProvider } from '@/contexts/sidebar-context'
import { FilterProvider } from '@/entities/avail/hooks/FilteringHook'
import { env } from '@/env'
import { queryClient } from '@/lib/react-query'
import { RateSelectionProvider } from '@/pages/avail/hooks/rate-selecion-context'
import useScrollToTop from '@/shared/hooks/useScrollToTop'

import { httpClient } from '../adapters/axios/axios'
import { AvailVipProvider } from './availVip'
import { CancelBookingProvider } from './cancelBooking'
import { NotificationProvider } from './notification'
import { RequestCancelEditProvider } from './requestCancelEdit'
import { SidebarProvider } from './sideBar'

interface AppProviderProps {
  children: React.ReactNode
}

function AppProvider({ children }: AppProviderProps): JSX.Element {
  useScrollToTop()
  return (
    <NuqsAdapter>
      <AvailContextProvider>
        <httpClient.AxiosInterceptor>
          <httpClient.AxiosInterceptor>
            <QueryClientProvider client={queryClient}>
              <IdleController timeout={1000 * 60 * 60 * 8}>
                <RateSelectionProvider>
                  <B2BTourProvider>
                    <NotificationProvider>
                      <AvailVipProvider>
                        <MapProvider>
                          <AutocompleteProvider>
                            <MarkersProvider>
                              <FilterProvider>
                                <FilesContextProvider>
                                  <RequestCancelEditProvider>
                                    <CancelBookingProvider>
                                      <SidebarContextProvider>
                                        <AuthContextProvider>
                                          <SidebarProvider>
                                            {children}
                                          </SidebarProvider>
                                        </AuthContextProvider>
                                      </SidebarContextProvider>
                                    </CancelBookingProvider>
                                  </RequestCancelEditProvider>
                                </FilesContextProvider>
                              </FilterProvider>
                            </MarkersProvider>
                          </AutocompleteProvider>
                        </MapProvider>
                      </AvailVipProvider>
                    </NotificationProvider>
                  </B2BTourProvider>
                </RateSelectionProvider>
              </IdleController>
            </QueryClientProvider>
          </httpClient.AxiosInterceptor>
        </httpClient.AxiosInterceptor>
        <Toaster />
      </AvailContextProvider>
    </NuqsAdapter>
  )
}

const isAuthenticated = async (): Promise<boolean> => {
  try {
    const user = await getCurrentUser()
    Sentry.setUser({
      id: user.username,
    })

    return true
  } catch {
    return false
  }
}

const AuthenticatedOrRedirect: React.FC<AppProviderProps> = props => {
  React.useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      const isAuth = await isAuthenticated()
      if (!isAuth) {
        window.location.href = `${env.VITE_HUB_ROUTE}/dashboard`
      }
    }

    checkAuth()
  }, [])

  return <AppProvider {...props} />
}

const ExportedComponent: React.FC<AppProviderProps> = AuthenticatedOrRedirect

export default ExportedComponent
